/**
 * Temporary: remove discontinued bundle bags (+ paired holsters) from existing carts.
 * Deprecated child bag product IDs come from a manual collection
 * (theme setting deprecated_bundle_child_products).
 * Gated by theme setting `enable_cart_cleanup`.
 */

const config = window.deprecatedBundleCleanup || {};
const deprecatedProductIds = new Set((config.productIds || []).map(Number));
const notificationsEnabled = config.showNotification !== false;

const isDeprecatedProduct = (item) => deprecatedProductIds.has(item.product_id);

const findPairedHolster = (items, bag) => {
  const bundleId = bag.properties?.BundleId;
  const bagSku = bag.sku;
  if (!bundleId || !bagSku) return null;

  return items.find(
    (item) =>
      item.key !== bag.key &&
      item.properties?.BundleId === bundleId &&
      item.properties?.["BAG-SKU"] === bagSku
  );
};

const collectRemovals = (items) => {
  const updates = {};
  const removals = [];

  for (const item of items) {
    if (!isDeprecatedProduct(item)) continue;

    const holster = findPairedHolster(items, item);
    updates[item.key] = 0;
    if (holster) updates[holster.key] = 0;

    removals.push({ bag: item, holster: holster || null });
  }

  return { updates, removals };
};

const summarize = ({ bag, holster }) => ({
  bag: bag.product_title,
  holster: holster?.product_title || null,
});

const showNotification = (count) => {
  const message =
    count === 1
      ? "An unavailable bundle item was removed from your cart."
      : `${count} unavailable items were removed from your cart.`;

  const banner = document.createElement("div");
  banner.className = "banner banner--warning justify-center";
  banner.setAttribute("role", "alert");
  banner.style.cssText =
    "position:fixed;top:calc(var(--header-height,0px) + 1rem);left:50%;transform:translateX(-50%);z-index:9999;max-width:min(36rem,calc(100vw - 2rem));box-shadow:0 8px 24px rgba(0,0,0,.15);";
  banner.innerHTML = `
    <svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-warning" style="--icon-height:18px" viewBox="0 0 18 18">
      <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
      <path d="M9 4.5C9.41421 4.5 9.75 4.83579 9.75 5.25V9.75C9.75 10.1642 9.41421 10.5 9 10.5C8.58579 10.5 8.25 10.1642 8.25 9.75V5.25C8.25 4.83579 8.58579 4.5 9 4.5Z" fill="#ffffff"></path>
      <path d="M9 13.5C9.41421 13.5 9.75 13.1642 9.75 12.75C9.75 12.3358 9.41421 12 9 12C8.58579 12 8.25 12.3358 8.25 12.75C8.25 13.1642 8.58579 13.5 9 13.5Z" fill="#ffffff"></path>
    </svg>
    <p style="color:#000;">${message}</p>
  `;

  document.body.appendChild(banner);

  setTimeout(async () => {
    await banner.animate({ opacity: [1, 0] }, { duration: 250, fill: "forwards" }).finished;
    banner.remove();
  }, 6000);
};

const queueNotification = (count) => {
  try {
    sessionStorage.setItem("deprecatedBundleCleanupNotice", String(count));
  } catch (_) {
    showNotification(count);
  }
};

const showQueuedNotification = () => {
  if (!notificationsEnabled) return;

  try {
    const count = Number(sessionStorage.getItem("deprecatedBundleCleanupNotice"));
    if (!count) return;
    sessionStorage.removeItem("deprecatedBundleCleanupNotice");
    showNotification(count);
  } catch (_) {}
};

const removeFromCart = async (updates) => {
  const res = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ updates }),
  });

  if (!res.ok) throw new Error(`cart/update.js ${res.status}`);
};

const run = async () => {
  if (!deprecatedProductIds.size) return;

  try {
    const cart = await fetch("/cart.js").then((r) => r.json());
    const items = cart.items || [];
    if (!items.length) return;

    const { updates, removals } = collectRemovals(items);
    if (!removals.length) return;

    await removeFromCart(updates);
    console.info("[Deprecated Bundle Cleanup] removed:", removals.map(summarize));

    const removedCount = Object.keys(updates).length;
    const isCartPage = /(^|\/)cart(\/|$)/.test(location.pathname);

    if (isCartPage) {
      if (notificationsEnabled) queueNotification(removedCount);
      location.reload();
    } else {
      if (notificationsEnabled) showNotification(removedCount);
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  } catch (err) {
    console.error("[Deprecated Bundle Cleanup]", err);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    showQueuedNotification();
    run();
  });
} else {
  showQueuedNotification();
  run();
}
